import {
  timelineData,
  values,
  team,
  type TimelineItem,
  type TimelineDivider,
} from '@/data/about'
import { SEO } from '@/components/seo'
import { SignupCta } from '@/components/ui'

function isTimelineDivider(item: TimelineItem | TimelineDivider): item is TimelineDivider {
  return 'type' in item && item.type === 'divider'
}

function TimelineCard({ item }: { item: TimelineItem }) {
  return (
    <div
      className="timeline-item"
      style={{
        display: 'flex',
        justifyContent: item.side === 'left' ? 'flex-end' : 'flex-start',
        paddingRight: item.side === 'left' ? 'calc(50% + 30px)' : 0,
        paddingLeft: item.side === 'right' ? 'calc(50% + 30px)' : 0,
        marginBottom: 40,
        position: 'relative',
      }}
    >
      {/* Dot */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          width: item.highlight ? 16 : 12,
          height: item.highlight ? 16 : 12,
          borderRadius: '50%',
          background: item.future ? 'transparent' : item.highlight ? '#f59e0b' : '#14b8a6',
          border: item.future ? '2px dashed #14b8a6' : '3px solid #ffffff',
          boxShadow: item.highlight
            ? '0 0 20px rgba(245,158,11,0.5)'
            : item.future
              ? 'none'
              : '0 0 12px rgba(20,184,166,0.3)',
        }}
      />

      {/* Card */}
      <div
        className="timeline-card"
        style={{
          background: item.highlight
            ? 'linear-gradient(145deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.03) 100%)'
            : '#ffffff',
          border: item.future
            ? '1px dashed rgba(20,184,166,0.3)'
            : item.highlight
              ? '1px solid rgba(245,158,11,0.3)'
              : '1px solid #e2e8f0',
          borderRadius: 12,
          padding: '20px 24px',
          maxWidth: 350,
          transition: 'all 0.3s ease',
          opacity: item.future ? 0.7 : 1,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: item.future ? '#475569' : item.highlight ? '#f59e0b' : '#14b8a6',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 6,
          }}
        >
          {item.date}
        </div>
        <div
          style={{
            fontSize: 17,
            fontWeight: 600,
            color: '#0f172a',
            marginBottom: 8,
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#475569',
            lineHeight: 1.6,
          }}
        >
          {item.description}
        </div>
      </div>
    </div>
  )
}

function TimelineDividerComponent({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 40,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 2,
          background:
            'linear-gradient(90deg, transparent 0%, #f59e0b 30%, #f59e0b 70%, transparent 100%)',
        }}
      />
      <div
        style={{
          background: '#ffffff',
          padding: '8px 24px',
          borderRadius: 20,
          border: '2px solid #f59e0b',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <span
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 14,
            fontWeight: 500,
            fontStyle: 'italic',
            color: '#f59e0b',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </span>
      </div>
    </div>
  )
}

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About WealthSpace | Built by Advisers, for Advisers"
        description="Meet the team behind WealthSpace. Founded by financial advisers who understand your challenges. Join the Adviser Equity Group and own part of the future."
        keywords="WealthSpace team, about WealthSpace, financial adviser owned software, IFA technology company UK, adviser equity group, Max Anderson WealthSpace, fintech for advisers UK"
        url="/about"
      />

      {/* Hero */}
      <section className="pt-[100px] pb-[80px] px-6 relative overflow-hidden">
        <div className="max-w-[800px] mx-auto relative z-[1]">
          <h1 className="font-serif text-[clamp(36px,5vw,52px)] font-normal leading-[1.1] text-brand-light text-center mb-6">
            Helping advisers do <span className="text-accent-teal italic">more</span> of what matters
          </h1>
          <div
            className="rounded-2xl max-w-[650px] mx-auto text-center"
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              padding: '32px 40px',
            }}
          >
            <p className="text-[20px] text-slate-600 leading-[1.7] mb-4">
              We're on a mission to give financial advisers their time back. For some that's bath
              time. For others it's the gym, the garden, or just a quiet evening. And for some, it's
              50% more clients.
            </p>
            <p className="text-base text-accent-teal italic">
              Our founder, a dad of three, built this because he needed it.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline - Our Story */}
      <section style={{ padding: '80px 24px', maxWidth: 1000, margin: '0 auto' }}>
        <h2
          className="font-serif"
          style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            fontWeight: 400,
            color: '#0f172a',
            textAlign: 'center',
            marginBottom: 20,
          }}
        >
          Our <span style={{ color: '#14b8a6' }}>story</span>
        </h2>
        <p
          style={{
            fontSize: 16,
            color: '#475569',
            lineHeight: 1.8,
            textAlign: 'center',
            maxWidth: 700,
            margin: '0 auto 50px',
          }}
        >
          It hasn't been plain sailing. We've been resolutely bootstrapped - building quietly in
          the background, listening and observing, accumulating a reservoir of IP, knowledge and
          systems. Now the dam is bursting as we head into a super exciting 2026.
        </p>

        <div style={{ position: 'relative' }}>
          {/* Timeline line */}
          <div
            className="timeline-line"
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 2,
              height: '100%',
              background: 'linear-gradient(180deg, #14b8a6 0%, rgba(20,184,166,0.15) 100%)',
            }}
          />

          {/* Timeline items */}
          {timelineData.map((item, index) =>
            isTimelineDivider(item) ? (
              <TimelineDividerComponent key={index} label={item.label} />
            ) : (
              <TimelineCard key={index} item={item} />
            )
          )}
        </div>
      </section>

      {/* A word from our CEO */}
      <section
        className="py-20 px-6 relative z-[1]"
      >
        <div className="max-w-[800px] mx-auto">
          <h2 className="font-serif text-4xl font-normal text-center text-brand-light mb-10">
            A word from our <span className="text-accent-teal italic">CEO</span>
          </h2>
          <div className="bg-white border border-slate-200 rounded-2xl p-10 relative">
            <div
              className="absolute font-[Georgia,serif] text-accent-teal"
              style={{ fontSize: 48, opacity: 0.3, top: 20, left: 30 }}
            >
              "
            </div>
            <div className="space-y-5 text-[17px] text-brand-muted leading-[1.9] pl-5">
              <p>
                I am very proud of the technology we have built, and pleased to have a growing team
                supporting WealthSpace. As a financial adviser I was adamant that what we built had
                to follow three central tenets.
              </p>
              <p>
                <strong className="text-brand-light">First</strong>, we are not sharing client data
                with external AI companies whose products are predicated on the consumption of data -
                regardless of security arrangements in place.
              </p>
              <p>
                <strong className="text-brand-light">Second</strong>, the technology needs to be
                simple, easy to adopt and work within established businesses. In short - real-world
                proof.
              </p>
              <p>
                <strong className="text-brand-light">Lastly</strong>, it needs to do the job. By
                which I mean it needs to make advisers lives easier.
              </p>
            </div>
            <div className="flex items-center gap-4 pl-5 mt-6">
              <img
                src="/mugshots/Max.jpg"
                alt="Max Anderson"
                className="w-[100px] h-[100px] rounded-full object-cover border-[3px] border-accent-teal/30"
              />
              <div>
                <div className="font-semibold text-brand-light">Max Anderson</div>
                <div className="text-sm text-accent-teal">CEO & Founder</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-6 bg-slate-50 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-4xl font-normal text-center text-brand-light mb-12">
            Our <span className="text-accent-teal">values</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 rounded-2xl p-8 transition-all duration-400 hover:-translate-y-1 hover:border-accent-teal/20 hover:shadow-lg"
              >
                <h3 className="font-serif text-xl font-medium text-brand-light mb-3">
                  {value.title}
                </h3>
                <p className="text-[15px] text-brand-muted leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 px-6 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-4xl font-normal text-center text-brand-light mb-12">
            Leadership <span className="text-accent-teal">team</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="text-center">
                <div
                  className="w-[120px] h-[120px] rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-200"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(20, 184, 166, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
                  }}
                >
                  <span className="text-accent-teal font-semibold text-[32px] font-serif">
                    {member.initials}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-brand-light mb-1">{member.name}</h3>
                <p className="text-sm text-accent-teal mb-3">{member.role}</p>
                <p className="text-sm text-brand-muted leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Adviser Equity Group */}
      <section
        className="py-20 px-6 relative z-[1]"
      >
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="font-serif text-[40px] font-normal text-brand-light mb-6">
            The Adviser <span className="text-accent-teal">Equity Group</span>
          </h2>
          <p className="text-lg text-brand-muted leading-[1.8] mb-4">
            WealthSpace isn't just built for advisers - it's owned by them. Our Adviser Equity
            Group gives users a real stake in the platform's success and a voice in its future.
          </p>
          <p className="text-lg text-accent-teal italic">
            Built by Advisers. Run by Advisers. Owned by Advisers.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center relative z-[1]">
        <h2 className="font-serif text-[32px] font-normal text-brand-light mb-4">
          Ready to reclaim your <span className="text-accent-teal italic">time</span>?
        </h2>
        <p className="text-lg text-brand-muted max-w-[500px] mx-auto mb-8">
          Join the advisers who are already working smarter, not harder.
        </p>
        <SignupCta location="about_cta">Start Free Trial</SignupCta>
      </section>
    </>
  )
}
